import { useState, useEffect, useCallback, useRef } from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { detectCardType } from '../../utils/cardBrand';
import CardBrandIcon from './CardBrandIcon';
import CardNumberInput from './CardNumberInput';
import SquareCardEntry from './SquareCardEntry';
import AddressAutocompleteInput from './AddressAutocompleteInput';
import '../AllCss/CardNumberInput.css';

const DEFAULT_COUNTRY = 'United States';

function emptyCardFields() {
  return {
    nickname: '',
    cardholderName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    zipCode: '',
  };
}

function addressDefaults(user, card = null) {
  return {
    address: card?.address || user?.address || '',
    address2: card?.address2 || user?.address2 || '',
    city: card?.city || user?.city || '',
    state: card?.state || user?.state || '',
    country: card?.country || DEFAULT_COUNTRY,
  };
}

function profileDefaults(user, card = null) {
  return {
    ...emptyCardFields(),
    cardholderName: user?.fullName || '',
    ...addressDefaults(user, card),
  };
}

function buildBillingPayload(cardForm) {
  return {
    nickname: cardForm.nickname || null,
    address: cardForm.address.trim() || null,
    address2: cardForm.address2.trim() || null,
    city: cardForm.city.trim() || null,
    state: cardForm.state || null,
    country: cardForm.country.trim() || null,
    zipCode: cardForm.zipCode.trim() || null,
    zipcode: cardForm.zipCode.trim() || null,
  };
}

export default function InterestedInCardForm({
  submitLabel,
  payHint = '',
  submittingLabel = 'Processing…',
  onSubmit,
  onError,
  user,
  savedCards = [],
  loadingCards = false,
  showStoreCardCheckbox = true,
  storeCardLabel = 'Save this card to Payment Methods',
  storeCardHint = 'Shows under Profile → Payment Methods — Manage your credit and debit cards.',
  allowSavedCardPay = true,
}) {
  const [cardForm, setCardForm] = useState(() => profileDefaults(user));
  const [selectedCardId, setSelectedCardId] = useState('');
  const [storeCard, setStoreCard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [squareConfig, setSquareConfig] = useState(null);
  const [loadingSquare, setLoadingSquare] = useState(true);
  const didInitialPrefill = useRef(false);

  const vaultReadyCards = savedCards.filter((c) => !c.requiresRevault);

  useEffect(() => {
    api
      .get('/payment-cards/square-config')
      .then((res) => {
        if (res.data.success) setSquareConfig(res.data.data);
      })
      .catch(() => setSquareConfig(null))
      .finally(() => setLoadingSquare(false));
  }, []);

  const applyAddressFromCard = useCallback(
    (card) => {
      if (!card) {
        setSelectedCardId('');
        setCardForm((prev) => ({
          ...profileDefaults(user),
          nickname: prev.nickname,
        }));
        return;
      }
      setSelectedCardId(card.id);
      setCardForm((prev) => ({
        ...profileDefaults(user, card),
        nickname: prev.nickname || card.nickname || '',
        cardholderName: user?.fullName || '',
      }));
      setStoreCard(false);
    },
    [user]
  );

  useEffect(() => {
    if (loadingCards || didInitialPrefill.current) return;
    didInitialPrefill.current = true;
    if (!allowSavedCardPay) {
      setCardForm(profileDefaults(user));
      return;
    }
    const usable = vaultReadyCards.length > 0 ? vaultReadyCards : savedCards;
    if (usable.length > 0) {
      const defaultCard = usable.find((c) => c.isDefault) || usable[0];
      applyAddressFromCard(defaultCard);
    } else {
      setCardForm(profileDefaults(user));
    }
  }, [loadingCards, savedCards, vaultReadyCards, user, applyAddressFromCard, allowSavedCardPay]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === 'expiryDate') {
      const digits = String(value).replace(/\D/g, '').slice(0, 4);
      next = digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    if (name === 'cvv') next = String(value).replace(/\D/g, '').slice(0, 4);
    setCardForm((f) => ({ ...f, [name]: next }));
  };

  const submitWithPayload = async (extra) => {
    setSaving(true);
    try {
      await onSubmit({
        storeCard: showStoreCardCheckbox ? storeCard : false,
        cardholderName: cardForm.cardholderName.trim() || user?.fullName,
        isDefault: true,
        ...buildBillingPayload(cardForm),
        ...extra,
      });
    } catch (err) {
      onError(err.response?.data?.message || err.message || 'Payment failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSavedCardSubmit = async (e) => {
    e.preventDefault();
    onError('');
    if (!selectedCardId) {
      onError('Select a saved card or enter a new card below.');
      return;
    }
    await submitWithPayload({ paymentCardId: selectedCardId });
  };

  const handleSquareToken = async (squareSourceId) => {
    onError('');
    if (!cardForm.cardholderName.trim() && !user?.fullName) {
      onError('Cardholder name is required.');
      return;
    }
    await submitWithPayload({
      squareSourceId,
      paymentCardId: undefined,
    });
  };

  const handleMockSubmit = async (e) => {
    e.preventDefault();
    onError('');
    if (!cardForm.cardholderName.trim()) {
      onError('Cardholder name is required.');
      return;
    }
    const parts = cardForm.expiryDate.split('/');
    if (parts.length !== 2) {
      onError('Expiry must be MM/YY');
      return;
    }
    const expiryMonth = parseInt(parts[0], 10);
    const expiryYear = parseInt(`20${parts[1]}`, 10);
    await submitWithPayload({
      mockCard: true,
      cardLast4: cardForm.cardNumber.replace(/\D/g, '').slice(-4) || '4242',
      cardType: detectCardType(cardForm.cardNumber),
      expiryMonth,
      expiryYear,
    });
  };

  const useSquare = Boolean(
    squareConfig?.inlinePaymentsEnabled &&
      squareConfig?.applicationId &&
      squareConfig?.locationId
  );
  /** Dev-only plain inputs — only when Square Web SDK cannot load (missing app/location IDs) */
  const useMock = Boolean(squareConfig?.squareMock && !useSquare);
  const detectedBrand = detectCardType(cardForm.cardNumber);

  const nicknameField = (
    <div className="interested-step2-field">
      <label htmlFor="ii-nickname">Nickname (optional)</label>
      <input
        id="ii-nickname"
        name="nickname"
        value={cardForm.nickname}
        onChange={handleChange}
        placeholder="Work card, personal Visa…"
        autoComplete="off"
      />
    </div>
  );

  const cardholderField = (
    <div className="interested-step2-field">
      <label htmlFor="ii-name">Cardholder name *</label>
      <input
        id="ii-name"
        name="cardholderName"
        value={cardForm.cardholderName}
        onChange={handleChange}
        autoComplete="cc-name"
        placeholder="Full name on card"
        required
      />
    </div>
  );

  const billingAddressFields = () => (
    <div className="interested-step2-billing-address">
      <AddressAutocompleteInput
        id="ii-address-autocomplete"
        label="Billing address"
        value={{
          address: cardForm.address,
          address2: cardForm.address2,
          city: cardForm.city,
          state: cardForm.state,
          zipCode: cardForm.zipCode,
          country: cardForm.country,
        }}
        onChange={(patch) =>
          setCardForm((f) => ({
            ...f,
            ...patch,
            country: patch.country || f.country || DEFAULT_COUNTRY,
          }))
        }
        includeCounty={false}
        className="interested-step2-field"
        labelClassName="interested-step2-billing-address-title"
        inputClassName=""
        placeholder="Start typing your billing address…"
      />
    </div>
  );

  const storeCardCheckbox =
    showStoreCardCheckbox && !selectedCardId && (useSquare || useMock) ? (
      <div className="interested-step2-store-plain">
        <label className="interested-step2-store-plain-label" htmlFor="ii-store-card">
          <input
            type="checkbox"
            id="ii-store-card"
            className="interested-step2-store-checkbox"
            checked={storeCard}
            onChange={(e) => setStoreCard(e.target.checked)}
          />
          <span className="interested-step2-store-plain-text">
            <span className="interested-step2-store-plain-title">{storeCardLabel}</span>
            <span className="interested-step2-store-plain-hint">{storeCardHint}</span>
          </span>
        </label>
      </div>
    ) : null;

  const mockCardFields = (
    <>
      <div className="interested-step2-card-details">
        <div className="interested-step2-field interested-step2-field--card-number">
          <label htmlFor="ii-number">Card number *</label>
          <CardNumberInput
            id="ii-number"
            name="cardNumber"
            value={cardForm.cardNumber}
            onChange={handleChange}
            autoComplete="off"
            placeholder="3782 822463 10005"
            required
            inputClassName="interested-step2-card-number-input"
          />
        </div>
      </div>
      <div className="interested-step2-grid--exp-cvv">
        <div className="interested-step2-field">
          <label htmlFor="ii-exp">Expiration *</label>
          <input
            id="ii-exp"
            name="expiryDate"
            value={cardForm.expiryDate}
            onChange={handleChange}
            placeholder="MM / YY"
            required
          />
        </div>
        <div className="interested-step2-field">
          <label htmlFor="ii-cvv">Security code *</label>
          <input
            id="ii-cvv"
            name="cvv"
            value={cardForm.cvv}
            onChange={handleChange}
            inputMode="numeric"
            autoComplete="off"
            placeholder={detectedBrand === 'amex' ? '4 digits' : '3 digits'}
            required
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="interested-step2-card-form">
      {loadingCards && allowSavedCardPay && (
        <p className="interested-step2-cards-loading">Loading your saved cards…</p>
      )}

      {allowSavedCardPay && vaultReadyCards.length > 0 && (
        <div className="interested-step2-saved-cards">
          <p className="interested-step2-saved-cards-title">Pay with a saved card</p>
          <ul className="interested-step2-saved-cards-list">
            {vaultReadyCards.map((card) => (
              <li key={card.id}>
                <label className="interested-step2-saved-card-option">
                  <input
                    type="radio"
                    name="savedCard"
                    checked={selectedCardId === card.id}
                    onChange={() => applyAddressFromCard(card)}
                  />
                  <CardBrandIcon type={card.cardType} />
                  <span>
                    {card.nickname || `${(card.cardType || 'card').toUpperCase()}`} ••••{' '}
                    {card.cardNumberLast4}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {selectedCardId && (
            <form onSubmit={handleSavedCardSubmit}>
              <button
                type="button"
                className="interested-step2-use-new-card"
                onClick={() => applyAddressFromCard(null)}
              >
                Use a different card instead
              </button>
              {nicknameField}
              {cardholderField}
              {billingAddressFields()}
              <button type="submit" className="interested-step2-primary" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 size={18} className="interested-step2-spin" /> {submittingLabel}
                  </>
                ) : (
                  <>
                    <CreditCard size={18} /> {submitLabel}
                  </>
                )}
              </button>
            </form>
          )}
          <p className="interested-step2-or-divider">Or use a new card</p>
        </div>
      )}

      {savedCards.some((c) => c.requiresRevault) && allowSavedCardPay && (
        <p className="interested-step2-revault-note">
          Some saved cards must be re-added using the secure form below (PCI update).
        </p>
      )}

      {!selectedCardId && (
        <>
          {nicknameField}
          {cardholderField}

          {loadingSquare && <p className="interested-step2-cards-loading">Loading secure payment…</p>}

          {useSquare && !loadingSquare && (
            <SquareCardEntry
              applicationId={squareConfig.applicationId}
              locationId={squareConfig.locationId}
              postalCode={cardForm.zipCode}
              submitLabel={submitLabel}
              submittingLabel={submittingLabel}
              payHint={payHint}
              disabled={saving}
              onToken={handleSquareToken}
              onError={onError}
            >
              {billingAddressFields()}
              {storeCardCheckbox}
            </SquareCardEntry>
          )}

          {useMock && !loadingSquare && (
            <form onSubmit={handleMockSubmit}>
              {mockCardFields}
              {billingAddressFields()}
              {storeCardCheckbox}
              <button type="submit" className="interested-step2-primary" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 size={18} className="interested-step2-spin" /> {submittingLabel}
                  </>
                ) : (
                  <>
                    <CreditCard size={18} /> {submitLabel}
                  </>
                )}
              </button>
              {payHint ? <p className="interested-step2-pay-hint">{payHint}</p> : null}
            </form>
          )}

          {!useSquare && !useMock && !loadingSquare && (
            <p className="interested-step2-square-unavailable">
              Secure card entry is unavailable. Check Square configuration or try again later.
            </p>
          )}
        </>
      )}
    </div>
  );
}
